/**
 * @file    metricsManagerModel.ts
 * @scope   CPR
 * @summary Control-state strategies and display helpers for the MetricsManager view.
 * @consumes ControlState, RollupMetricModel
 * @produces RollupItemState, RollupGlobalState, compute* strategies, RollupValidationError,
 *           getRollupErrors, getRollupErrorMessage, getProgramCountLabel
 * @sideEffects None (pure).
 */
import { ControlState } from '../../../../common/models/controlState';
import { CPR_MAX_ROLLUP_METRICS } from '../../constants';
import {
  RollupMetricModel,
  getRollupProgramCount,
} from 'models/CrossProgramRollup/RollupMetricModel';

export interface RollupItemState {
  Drag: ControlState;
  Delete: ControlState;
  ManagePrograms: ControlState;
}

export interface RollupGlobalState {
  AddRollup: ControlState;
}

/** Per-row strategy: drag only makes sense with 2+ rows. */
export const computeRollupItemState = (
  _item: RollupMetricModel,
  _index: number,
  items: RollupMetricModel[]
): RollupItemState => ({
  Drag: { visible: true, disabled: items.length <= 1 },
  Delete: { visible: true, disabled: false },
  ManagePrograms: { visible: true, disabled: false },
});

/**
 * List-level strategy. "Add" is hidden once the cap is reached and comes back when a rollup is
 * removed. WHY: the backend allows a single rollup per widget (`CPR_MAX_ROLLUP_METRICS`).
 */
export const computeRollupGlobalState = (items: RollupMetricModel[]): RollupGlobalState => ({
  AddRollup: { visible: items.length < CPR_MAX_ROLLUP_METRICS, disabled: false },
});

/** Rules a rollup metric can break. */
export type RollupValidationError = 'missingName' | 'noPrograms';

const ROLLUP_ERROR_MESSAGES: Record<RollupValidationError, string> = {
  missingName: 'A name is required.',
  noPrograms: 'At least one program is required.',
};

/** @returns Every rule the metric breaks; empty = valid. */
export const getRollupErrors = (item: RollupMetricModel): RollupValidationError[] => {
  const errors: RollupValidationError[] = [];
  if (!item.displayName.trim()) errors.push('missingName');
  if (getRollupProgramCount(item) === 0) errors.push('noPrograms');
  return errors;
};

/** @returns The tooltip text for the given errors, one per line. */
export const getRollupErrorMessage = (errors: RollupValidationError[]): string =>
  errors.map((error) => ROLLUP_ERROR_MESSAGES[error]).join('\n');

/** Builds the "N programs" counter label. */
export const getProgramCountLabel = (count: number): string =>
  `${count} ${count === 1 ? 'program' : 'programs'}`;
